import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

function InsightMarketEntry() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-btacc-blue z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Link to="/" className="flex-shrink-0">
              <h1 className="text-2xl font-montserrat font-bold text-white">
                BT<span className="text-btacc-gold">ACC</span>
              </h1>
              <p className="text-xs text-btacc-light font-inter -mt-1">Brazil Tax Advisory & Compliance Center</p>
            </Link>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-btacc-blue hover:text-btacc-gold transition mb-8">
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>

          {/* Article Header */}
          <div className="mb-8">
            <div className="text-sm text-btacc-secondary mb-4">BTACC Insights</div>
            <h1 className="text-4xl md:text-5xl font-montserrat font-bold text-btacc-blue mb-6">
              Market Entry Strategy: What U.S. Companies Need to Know
            </h1>
            <p className="text-xl text-btacc-graphite font-inter">
              Planning to expand to Brazil? Our comprehensive guide covers regulatory requirements, tax implications, and operational considerations.
            </p>
          </div>

          {/* Featured Image */}
          <img 
            src="/images/btacc-insight.png"
            alt="BTACC - Brazil Tax Advisory & Compliance Center"
            className="w-full h-96 object-cover rounded-lg mb-12"
          />

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              Brazil represents Latin America's largest economy and offers significant opportunities for U.S. companies. However, successful market entry requires careful planning and deep understanding of the regulatory landscape. Here's your strategic roadmap.
            </p>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">Step 1: Market Research and Feasibility</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              Before committing resources, conduct thorough analysis:
            </p>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2"><strong>Market Size:</strong> Assess demand for your products/services</li>
              <li className="mb-2"><strong>Competition:</strong> Understand local and international competitors</li>
              <li className="mb-2"><strong>Regulatory Environment:</strong> Identify industry-specific requirements</li>
              <li className="mb-2"><strong>Total Landed Cost:</strong> Calculate true cost including all taxes and fees</li>
            </ul>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">Step 2: Choose Your Entry Model</h2>
            
            <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mt-6 mb-3">Direct Export</h3>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-4">
              <strong>Best for:</strong> Testing the market, established products<br/>
              <strong>Requirements:</strong> Brazilian importer, customs compliance<br/>
              <strong>Advantages:</strong> Lower risk, minimal investment
            </p>

            <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mt-6 mb-3">Local Distributor/Agent</h3>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-4">
              <strong>Best for:</strong> Market development, complex products<br/>
              <strong>Requirements:</strong> Distribution agreement, agent contracts<br/>
              <strong>Advantages:</strong> Local expertise, established networks
            </p>

            <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mt-6 mb-3">Brazilian Subsidiary</h3>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-4">
              <strong>Best for:</strong> Long-term commitment, significant volume<br/>
              <strong>Requirements:</strong> Corporate registration, tax ID (CNPJ), legal representation<br/>
              <strong>Advantages:</strong> Full control, local credibility
            </p>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">Step 3: Understand Tax Implications</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              Brazil's tax system is complex with multiple layers:
            </p>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2"><strong>Federal Taxes:</strong> II (Import Duty), IPI, PIS, COFINS</li>
              <li className="mb-2"><strong>State Tax:</strong> ICMS (varies by state, 7-18%)</li>
              <li className="mb-2"><strong>Municipal Tax:</strong> ISS (services)</li>
              <li className="mb-2"><strong>Total Tax Burden:</strong> Can reach 60-80% for some products</li>
            </ul>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">Step 4: Regulatory Compliance</h2>
            
            <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mt-6 mb-3">Essential Registrations</h3>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2">CNPJ (Brazilian Tax ID)</li>
              <li className="mb-2">RADAR (Customs registration for importers)</li>
              <li className="mb-2">State Registration (Inscrição Estadual)</li>
              <li className="mb-2">Municipal Registration (if applicable)</li>
            </ul>

            <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mt-6 mb-3">Industry-Specific Certifications</h3>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              Depending on your sector, you may need:
            </p>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2">ANVISA approval (health, food, cosmetics)</li>
              <li className="mb-2">INMETRO certification (product quality and safety)</li>
              <li className="mb-2">ANATEL approval (telecommunications)</li>
              <li className="mb-2">MAPA authorization (agriculture)</li>
            </ul>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">Step 5: Operational Setup</h2>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2"><strong>Banking:</strong> Open corporate account, understand currency controls</li>
              <li className="mb-2"><strong>Legal:</strong> Draft contracts compliant with Brazilian law</li>
              <li className="mb-2"><strong>HR:</strong> Understand labor laws (13th salary, FGTS, vacation rules)</li>
              <li className="mb-2"><strong>Logistics:</strong> Partner with reliable freight forwarders and customs brokers</li>
            </ul>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">Common Pitfalls to Avoid</h2>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2">Underestimating timeline (6-12 months typical for full setup)</li>
              <li className="mb-2">Ignoring local business culture and relationship-building</li>
              <li className="mb-2">Failing to budget for compliance costs (15-20% of operational budget)</li>
              <li className="mb-2">Not having local legal and tax advisors from day one</li>
            </ul>

            <div className="bg-btacc-light p-6 rounded-lg mt-8 mb-8">
              <h3 className="text-lg font-montserrat font-semibold text-btacc-blue mb-3">Market Entry Assessment</h3>
              <p className="text-btacc-graphite font-inter mb-4">
                BTACC offers comprehensive market entry assessments tailored to your industry and business model. Our 50+ years of expertise can help you navigate Brazil's complexity with confidence.
              </p>
              <button onClick={handleContactClick} className="inline-block bg-btacc-blue text-white px-6 py-3 rounded-md font-montserrat font-semibold hover:bg-btacc-secondary transition cursor-pointer">
                Schedule Your Assessment
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-btacc-blue text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-block mb-4">
            <h2 className="text-2xl font-montserrat font-bold">
              BT<span className="text-btacc-gold">ACC</span>
            </h2>
          </Link>
          <div className="text-sm text-btacc-light mb-4">
            <p>Brazil Tax Advisory & Compliance Center</p>
            <p>320 Decker Drive, Irving, Texas 75062</p>
          </div>
          <p className="text-sm text-btacc-light">
            © {new Date().getFullYear()} BTACC — All rights reserved. / Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default InsightMarketEntry;